import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import AddressSearchInput from '../components/AddressSearchInput';
import api, { 
  fetchFamilyMembers, createFamilyMember, updateFamilyMember, deleteFamilyMember,
  fetchActivities, createActivity, updateActivity, deleteActivity,
  fetchEvents, createEvent, updateEvent, deleteEvent,
  fetchCalendarSources, subscribeCalendar, uploadICalFile, deleteCalendarSource,
  fetchEventHistory, updateEventStatus
} from '../lib/api';
import { format, parseISO } from 'date-fns';
import { 
  Plus, MoreVertical, Edit2, Trash2, Users, Check, Dumbbell, 
  Calendar as CalendarIcon, Search, ListTodo, MapPin, Clock,
  Upload, Link2, FileUp, CloudDownload, Loader2,
  CircleDot, Waves, Music2, Footprints, Trophy, Target, Bike, Heart,
  History, CheckCircle2, XCircle, RotateCcw, Crown, Lock, ChevronRight, X,
  Filter, Layers, ArrowUpDown, Navigation, ExternalLink, Home
} from 'lucide-react';
import { toast } from 'sonner';

// Consistent FloatingWidget Component
const FloatingWidget = ({ children, className = '' }) => (
  <div
    className={`relative ${className}`}
    style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(40px)',
      WebkitBackdropFilter: 'blur(40px)',
      borderRadius: '16px',
      boxShadow: '0 2px 16px -4px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03)',
    }}
  >
    {children}
  </div>
);

// Color options
const CHILD_COLORS = [
  // Row 1 - Primary colors
  { value: '#10B981', name: 'Emerald' },
  { value: '#22C55E', name: 'Green' },
  { value: '#F97316', name: 'Orange' },
  { value: '#3B82F6', name: 'Blue' },
  { value: '#EC4899', name: 'Pink' },
  { value: '#8B5CF6', name: 'Purple' },
  // Row 2 - More colors
  { value: '#EF4444', name: 'Red' },
  { value: '#F59E0B', name: 'Amber' },
  { value: '#14B8A6', name: 'Teal' },
  { value: '#6366F1', name: 'Indigo' },
  { value: '#06B6D4', name: 'Cyan' },
  { value: '#84CC16', name: 'Lime' },
  // Row 3 - Darker/muted tones
  { value: '#0EA5E9', name: 'Sky' },
  { value: '#D946EF', name: 'Fuchsia' },
  { value: '#78716C', name: 'Stone' },
  { value: '#1E3A8A', name: 'Navy' },
  { value: '#7C3AED', name: 'Violet' },
  { value: '#DB2777', name: 'Rose' },
];

// Comprehensive Activity/Sports catalog with categories
const ACTIVITY_CATALOG = {
  'Ball Sports': [
    { name: 'Soccer', icon: '⚽', color: 'hsl(142, 71%, 45%)' },
    { name: 'Basketball', icon: '🏀', color: 'hsl(24, 95%, 53%)' },
    { name: 'Baseball', icon: '⚾', color: 'hsl(221, 83%, 53%)' },
    { name: 'Softball', icon: '🥎', color: 'hsl(340, 82%, 52%)' },
    { name: 'T-Ball', icon: '⚾', color: 'hsl(45, 93%, 47%)' },
    { name: 'Football', icon: '🏈', color: 'hsl(25, 95%, 40%)' },
    { name: 'Flag Football', icon: '🏈', color: 'hsl(340, 82%, 52%)' },
    { name: 'Volleyball', icon: '🏐', color: 'hsl(271, 81%, 56%)' },
    { name: 'Tennis', icon: '🎾', color: 'hsl(84, 81%, 44%)' },
    { name: 'Golf', icon: '⛳', color: 'hsl(142, 71%, 35%)' },
    { name: 'Rugby', icon: '🏉', color: 'hsl(0, 84%, 45%)' },
    { name: 'Lacrosse', icon: '🥍', color: 'hsl(199, 89%, 48%)' },
    { name: 'Field Hockey', icon: '🏑', color: 'hsl(328, 85%, 60%)' },
    { name: 'Ice Hockey', icon: '🏒', color: 'hsl(199, 89%, 38%)' },
    { name: 'Cricket', icon: '🏏', color: 'hsl(45, 93%, 47%)' },
    { name: 'Pickleball', icon: '🏓', color: 'hsl(174, 72%, 40%)' },
    { name: 'Badminton', icon: '🏸', color: 'hsl(271, 81%, 56%)' },
    { name: 'Table Tennis', icon: '🏓', color: 'hsl(0, 84%, 60%)' },
    { name: 'Handball', icon: '🤾', color: 'hsl(24, 95%, 53%)' },
  ],
  'Water Sports': [
    { name: 'Swimming', icon: '🏊', color: 'hsl(199, 89%, 48%)' },
    { name: 'Diving', icon: '🤿', color: 'hsl(199, 89%, 38%)' },
    { name: 'Water Polo', icon: '🤽', color: 'hsl(199, 89%, 58%)' },
    { name: 'Synchronized Swimming', icon: '🏊‍♀️', color: 'hsl(328, 85%, 60%)' },
    { name: 'Surfing', icon: '🏄', color: 'hsl(174, 72%, 40%)' },
    { name: 'Kayaking', icon: '🛶', color: 'hsl(45, 93%, 47%)' },
    { name: 'Rowing', icon: '🚣', color: 'hsl(221, 83%, 53%)' },
    { name: 'Sailing', icon: '⛵', color: 'hsl(199, 89%, 48%)' },
  ],
  'Combat & Martial Arts': [
    { name: 'Karate', icon: '🥋', color: 'hsl(0, 84%, 45%)' },
    { name: 'Taekwondo', icon: '🥋', color: 'hsl(221, 83%, 53%)' },
    { name: 'Judo', icon: '🥋', color: 'hsl(45, 93%, 47%)' },
    { name: 'Jiu-Jitsu', icon: '🥋', color: 'hsl(271, 81%, 56%)' },
    { name: 'Wrestling', icon: '🤼', color: 'hsl(24, 95%, 53%)' },
    { name: 'Boxing', icon: '🥊', color: 'hsl(0, 84%, 60%)' },
    { name: 'Kickboxing', icon: '🥊', color: 'hsl(340, 82%, 52%)' },
    { name: 'Fencing', icon: '🤺', color: 'hsl(0, 0%, 40%)' },
    { name: 'Kung Fu', icon: '🥋', color: 'hsl(0, 84%, 35%)' },
  ],
  'Gymnastics & Dance': [
    { name: 'Gymnastics', icon: '🤸', color: 'hsl(328, 85%, 60%)' },
    { name: 'Dance', icon: '💃', color: 'hsl(340, 82%, 52%)' },
    { name: 'Ballet', icon: '🩰', color: 'hsl(328, 85%, 70%)' },
    { name: 'Hip Hop Dance', icon: '🕺', color: 'hsl(271, 81%, 56%)' },
    { name: 'Jazz Dance', icon: '💃', color: 'hsl(45, 93%, 47%)' },
    { name: 'Tap Dance', icon: '👠', color: 'hsl(0, 0%, 30%)' },
    { name: 'Cheerleading', icon: '📣', color: 'hsl(340, 82%, 52%)' },
    { name: 'Acrobatics', icon: '🤸‍♀️', color: 'hsl(24, 95%, 53%)' },
    { name: 'Rhythmic Gymnastics', icon: '🎀', color: 'hsl(328, 85%, 60%)' },
    { name: 'Trampoline', icon: '🤸', color: 'hsl(142, 71%, 45%)' },
  ],
  'Track & Field': [
    { name: 'Track & Field', icon: '🏃', color: 'hsl(0, 84%, 60%)' },
    { name: 'Running', icon: '🏃', color: 'hsl(24, 95%, 53%)' },
    { name: 'Cross Country', icon: '🏃‍♂️', color: 'hsl(142, 71%, 35%)' },
    { name: 'Sprints', icon: '💨', color: 'hsl(0, 84%, 60%)' },
    { name: 'Long Jump', icon: '🦘', color: 'hsl(45, 93%, 47%)' },
    { name: 'High Jump', icon: '⬆️', color: 'hsl(199, 89%, 48%)' },
    { name: 'Shot Put', icon: '🏋️', color: 'hsl(0, 0%, 40%)' },
    { name: 'Javelin', icon: '🎯', color: 'hsl(142, 71%, 45%)' },
    { name: 'Pole Vault', icon: '🥇', color: 'hsl(45, 93%, 47%)' },
  ],
  'Cycling & Wheeled Sports': [
    { name: 'Cycling', icon: '🚴', color: 'hsl(142, 71%, 45%)' },
    { name: 'BMX', icon: '🚲', color: 'hsl(24, 95%, 53%)' },
    { name: 'Mountain Biking', icon: '🚵', color: 'hsl(25, 95%, 35%)' },
    { name: 'Skateboarding', icon: '🛹', color: 'hsl(0, 0%, 30%)' },
    { name: 'Roller Skating', icon: '🛼', color: 'hsl(328, 85%, 60%)' },
    { name: 'Inline Skating', icon: '🛼', color: 'hsl(199, 89%, 48%)' },
    { name: 'Scootering', icon: '🛴', color: 'hsl(84, 81%, 44%)' },
  ],
  'Winter Sports': [
    { name: 'Ice Skating', icon: '⛸️', color: 'hsl(199, 89%, 68%)' },
    { name: 'Figure Skating', icon: '⛸️', color: 'hsl(328, 85%, 60%)' },
    { name: 'Skiing', icon: '⛷️', color: 'hsl(199, 89%, 48%)' },
    { name: 'Snowboarding', icon: '🏂', color: 'hsl(271, 81%, 56%)' },
    { name: 'Sledding', icon: '🛷', color: 'hsl(0, 84%, 45%)' },
    { name: 'Curling', icon: '🥌', color: 'hsl(0, 0%, 40%)' },
  ],
  'Racquet Sports': [
    { name: 'Squash', icon: '🎾', color: 'hsl(221, 83%, 53%)' },
    { name: 'Racquetball', icon: '🎾', color: 'hsl(199, 89%, 48%)' },
  ],
  'Outdoor & Adventure': [
    { name: 'Rock Climbing', icon: '🧗', color: 'hsl(25, 95%, 40%)' },
    { name: 'Hiking', icon: '🥾', color: 'hsl(142, 71%, 35%)' },
    { name: 'Archery', icon: '🏹', color: 'hsl(142, 71%, 45%)' },
    { name: 'Horseback Riding', icon: '🏇', color: 'hsl(25, 95%, 35%)' },
    { name: 'Fishing', icon: '🎣', color: 'hsl(199, 89%, 48%)' },
    { name: 'Camping', icon: '🏕️', color: 'hsl(142, 71%, 35%)' },
  ],
  'Mind Sports & Games': [
    { name: 'Chess', icon: '♟️', color: 'hsl(0, 0%, 20%)' },
    { name: 'Esports', icon: '🎮', color: 'hsl(271, 81%, 56%)' },
  ],
  'Other Activities': [
    { name: 'Scouts', icon: '🏕️', color: 'hsl(45, 93%, 35%)' },
    { name: 'Music Lessons', icon: '🎵', color: 'hsl(271, 81%, 56%)' },
    { name: 'Piano', icon: '🎹', color: 'hsl(0, 0%, 20%)' },
    { name: 'Guitar', icon: '🎸', color: 'hsl(25, 95%, 40%)' },
    { name: 'Violin', icon: '🎻', color: 'hsl(25, 95%, 35%)' },
    { name: 'Drama/Theater', icon: '🎭', color: 'hsl(340, 82%, 52%)' },
    { name: 'Art Class', icon: '🎨', color: 'hsl(340, 82%, 52%)' },
    { name: 'Coding/Robotics', icon: '🤖', color: 'hsl(199, 89%, 48%)' },
    { name: 'Debate', icon: '🎤', color: 'hsl(221, 83%, 53%)' },
    { name: 'STEM Club', icon: '🔬', color: 'hsl(142, 71%, 45%)' },
    { name: 'Book Club', icon: '📚', color: 'hsl(25, 95%, 40%)' },
    { name: 'Tutoring', icon: '📖', color: 'hsl(199, 89%, 48%)' },
    { name: 'Yoga', icon: '🧘', color: 'hsl(174, 72%, 40%)' },
    { name: 'Pilates', icon: '🧘‍♀️', color: 'hsl(328, 85%, 60%)' },
  ],
};

// Flatten catalog for easy searching
const ALL_ACTIVITIES = Object.entries(ACTIVITY_CATALOG).flatMap(([category, activities]) =>
  activities.map(a => ({ ...a, category }))
);

// Event types for stats tracking
const EVENT_TYPES = [
  { id: 'practice', name: 'Practice', icon: '🏃', color: 'bg-blue-100 text-blue-700' },
  { id: 'game', name: 'Game', icon: '🎯', color: 'bg-green-100 text-green-700' },
  { id: 'tournament', name: 'Tournament', icon: '🏆', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'tryout', name: 'Tryout', icon: '📋', color: 'bg-purple-100 text-purple-700' },
  { id: 'scrimmage', name: 'Scrimmage', icon: '⚔️', color: 'bg-orange-100 text-orange-700' },
  { id: 'camp', name: 'Camp/Clinic', icon: '🎪', color: 'bg-pink-100 text-pink-700' },
  { id: 'lesson', name: 'Lesson', icon: '📚', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'recital', name: 'Recital/Performance', icon: '🎭', color: 'bg-rose-100 text-rose-700' },
  { id: 'competition', name: 'Competition', icon: '🥇', color: 'bg-amber-100 text-amber-700' },
  { id: 'meeting', name: 'Meeting', icon: '👥', color: 'bg-gray-100 text-gray-700' },
];

// Fuzzy search function
const fuzzySearch = (query, items, keys = ['name']) => {
  if (!query.trim()) return items;
  const lowerQuery = query.toLowerCase();
  return items.filter(item =>
    keys.some(key => {
      const value = item[key]?.toLowerCase() || '';
      // Check if starts with query (higher priority)
      if (value.startsWith(lowerQuery)) return true;
      // Check if contains query
      if (value.includes(lowerQuery)) return true;
      // Simple fuzzy: check if all chars exist in order
      let queryIndex = 0;
      for (const char of value) {
        if (char === lowerQuery[queryIndex]) queryIndex++;
        if (queryIndex === lowerQuery.length) return true;
      }
      return false;
    })
  ).sort((a, b) => {
    // Sort by relevance: starts with > contains > fuzzy
    const aName = a.name?.toLowerCase() || '';
    const bName = b.name?.toLowerCase() || '';
    const aStarts = aName.startsWith(lowerQuery) ? 0 : 1;
    const bStarts = bName.startsWith(lowerQuery) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    const aContains = aName.includes(lowerQuery) ? 0 : 1;
    const bContains = bName.includes(lowerQuery) ? 0 : 1;
    return aContains - bContains;
  });
};

// Legacy activity types for backwards compatibility
const ACTIVITY_TYPES = [
  { value: 'hsl(142, 71%, 45%)', name: 'Soccer', icon: CircleDot },
  { value: 'hsl(24, 95%, 53%)', name: 'Basketball', icon: Target },
  { value: 'hsl(199, 89%, 48%)', name: 'Swimming', icon: Waves },
  { value: 'hsl(328, 85%, 60%)', name: 'Dance', icon: Music2 },
  { value: 'hsl(271, 81%, 56%)', name: 'Gymnastics', icon: Footprints },
  { value: 'hsl(84, 81%, 44%)', name: 'Tennis', icon: Trophy },
  { value: 'hsl(221, 83%, 53%)', name: 'Baseball', icon: Target },
  { value: 'hsl(0, 84%, 60%)', name: 'Running', icon: Bike },
];

// ============== FAMILY MEMBERS TAB ==============
const FamilyTab = ({ members, setMembers, isPro = false, events = [], activities = [] }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({ name: '', birth_month: '', birth_year: '', color: CHILD_COLORS[0].value, photo: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, memberId: null, memberName: '' });
  const [showCustomColor, setShowCustomColor] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef(null);

  // Month names for display
  const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Generate year options (last 25 years)
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 25 }, (_, i) => currentYear - i);

  // Format birth date for display
  const formatBirthDate = (month, year) => {
    if (!month && !year) return null;
    if (month && year) {
      return `${MONTHS.find(m => m.value === month)?.label || ''} ${year}`;
    }
    if (year) return year.toString();
    return MONTHS.find(m => m.value === month)?.label || '';
  };

  // Get stats for each member
  const getMemberStats = (memberId) => {
    const memberEvents = events.filter(e => e.child_id === memberId);
    const uniqueActivityIds = [...new Set(memberEvents.map(e => e.activity_id).filter(Boolean))];
    const memberActivities = activities.filter(a => uniqueActivityIds.includes(a.id));
    return {
      eventCount: memberEvents.length,
      activities: memberActivities
    };
  };

  const openDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({ 
        name: member.name, 
        birth_month: member.birth_month || '', 
        birth_year: member.birth_year || '', 
        color: member.color || CHILD_COLORS[0].value, 
        photo: member.photo || null 
      });
      setPhotoPreview(member.photo || null);
      setShowCustomColor(!CHILD_COLORS.find(c => c.value === member.color));
    } else {
      setEditingMember(null);
      setFormData({ name: '', birth_month: '', birth_year: '', color: CHILD_COLORS[members.length % CHILD_COLORS.length].value, photo: null });
      setPhotoPreview(null);
      setShowCustomColor(false);
    }
    setShowDialog(true);
  };

  // Compress image to thumbnail
  const compressImage = (file, maxWidth = 200, maxHeight = 200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64
          const base64 = canvas.toDataURL('image/jpeg', quality);
          resolve(base64);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('Image too large. Please select a smaller image.');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const compressed = await compressImage(file);
      setPhotoPreview(compressed);
      setFormData(prev => ({ ...prev, photo: compressed }));
      toast.success('Photo added!');
    } catch (error) {
      toast.error('Failed to process image');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, photo: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Please enter a name'); return; }
    try {
      const submitData = { ...formData };
      if (editingMember) {
        const updated = await updateFamilyMember(editingMember.id, submitData);
        setMembers(prev => prev.map(m => m.id === editingMember.id ? updated : m));
        toast.success('Updated successfully');
      } else {
        const created = await createFamilyMember(submitData);
        setMembers(prev => [...prev, created]);
        toast.success('Child added!');
      }
      setShowDialog(false);
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteFamilyMember(id);
      setMembers(prev => prev.filter(m => m.id !== id));
      toast.success('Removed');
      setDeleteConfirm({ open: false, memberId: null, memberName: '' });
    } catch (error) { toast.error('Failed to remove'); }
  };

  const openDeleteConfirm = (member) => {
    setDeleteConfirm({ open: true, memberId: member.id, memberName: member.name });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{members.length} family member{members.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => openDialog()} className="rounded-xl h-10 text-sm" data-testid="add-family-member-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Child
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
            <Users className="h-8 w-8 text-blue-500" />
          </div>
          <p className="text-gray-600 font-medium mb-1">No family members yet</p>
          <p className="text-gray-400 text-sm mb-4">Add your children to get started</p>
          <Button onClick={() => openDialog()} variant="outline" className="rounded-xl">
            <Plus className="mr-2 h-4 w-4" /> Add Your First Child
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {members.map((member) => {
            const stats = getMemberStats(member.id);
            return (
            <FloatingWidget key={member.id} className="p-4">
              <div className="flex items-start gap-4">
                {member.photo ? (
                  <img 
                    src={member.photo} 
                    alt={member.name} 
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0" style={{ backgroundColor: member.color }}>
                    {member.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-gray-900">{member.name}</div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                      {stats.eventCount} event{stats.eventCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {(member.birth_month || member.birth_year) && (
                    <div className="text-sm text-gray-500 mt-0.5">
                      Born: {formatBirthDate(member.birth_month, member.birth_year)}
                    </div>
                  )}
                  
                  {/* Activities linked to this child */}
                  {stats.activities.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {stats.activities.map(activity => (
                        <span 
                          key={activity.id}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg text-white font-medium"
                          style={{ backgroundColor: activity.color || '#5DADE2' }}
                        >
                          {activity.icon || '📅'} {activity.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {stats.activities.length === 0 && (
                    <p className="text-xs text-gray-400 mt-2 italic">No activities assigned yet</p>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl flex-shrink-0"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openDialog(member)}><Edit2 className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openDeleteConfirm(member)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Remove</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="h-1 rounded-full mt-3" style={{ backgroundColor: member.color }} />
            </FloatingWidget>
          );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg mx-4 rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">{editingMember ? 'Edit Child' : 'Add Child'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Photo Upload - PRO only */}
            <div className="flex justify-center">
              <div className="relative">
                {photoPreview ? (
                  <div className="relative">
                    <img src={photoPreview} alt="Preview" className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg" />
                    <button 
                      onClick={removePhoto}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div 
                    className="w-24 h-24 rounded-2xl flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-lg"
                    style={{ backgroundColor: formData.color }}
                  >
                    {formData.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                
                {isPro ? (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 shadow-lg"
                  >
                    {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  </button>
                ) : (
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-not-allowed" title="Photo upload is a Pro feature">
                    <Lock className="h-3.5 w-3.5" />
                  </div>
                )}
                
                <input 
                  ref={fileInputRef}
                  type="file" 
                  accept="image/*" 
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
            </div>
            
            {!isPro && (
              <p className="text-center text-xs text-gray-500">
                <Crown className="inline h-3 w-3 text-yellow-500 mr-1" />
                Photo upload available for Pro users
              </p>
            )}

            <div className="grid gap-2">
              <Label className="text-sm font-medium">Name</Label>
              <Input placeholder="Child's name" data-testid="family-member-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="h-11 rounded-xl" />
            </div>
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Birth Month & Year (optional)</Label>
              <div className="grid grid-cols-2 gap-3">
                <Select value={formData.birth_month?.toString() || 'none'} onValueChange={(v) => setFormData({ ...formData, birth_month: v === 'none' ? '' : parseInt(v) })}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No month</SelectItem>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={formData.birth_year?.toString() || 'none'} onValueChange={(v) => setFormData({ ...formData, birth_year: v === 'none' ? '' : parseInt(v) })}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No year</SelectItem>
                    {YEARS.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Color</Label>
              <div className="grid grid-cols-6 gap-2">
                {CHILD_COLORS.map(c => (
                  <button key={c.value} type="button" className={`h-9 w-full rounded-lg transition-transform active:scale-95 flex items-center justify-center ${formData.color === c.value ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`} style={{ backgroundColor: c.value }} onClick={() => { setFormData({ ...formData, color: c.value }); setShowCustomColor(false); }} data-testid="child-color-swatch">
                    {formData.color === c.value && <Check className="h-4 w-4 text-white" />}
                  </button>
                ))}
              </div>
              
              {/* Custom Color - PRO only */}
              <div className="mt-2">
                {isPro ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowCustomColor(!showCustomColor)}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed transition-all ${
                        showCustomColor ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500" />
                      <span className="text-sm font-medium text-gray-700">Custom Color</span>
                    </button>
                    
                    {showCustomColor && (
                      <input 
                        type="color" 
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-12 h-10 rounded-lg cursor-pointer border-0"
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 opacity-60">
                    <Lock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500">Custom colors for Pro users</span>
                    <Crown className="h-4 w-4 text-yellow-500" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl w-full sm:w-auto">Cancel</Button>
            <Button onClick={handleSubmit} className="rounded-xl w-full sm:w-auto" data-testid="save-family-member-button"><Check className="mr-2 h-4 w-4" />{editingMember ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, memberId: null, memberName: '' })}>
        <DialogContent className="sm:max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center">
                <Trash2 className="h-7 w-7 text-red-500" />
              </div>
            </div>
            <DialogTitle className="text-lg text-center">Remove Family Member</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-gray-600 text-sm">
              Are you sure you want to remove <span className="font-semibold text-gray-800">"{deleteConfirm.memberName}"</span>?
            </p>
            <p className="text-gray-400 text-xs mt-2">This will also remove all their events.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm({ open: false, memberId: null, memberName: '' })} 
              className="rounded-xl w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleDelete(deleteConfirm.memberId)} 
              className="rounded-xl w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== ACTIVITIES TAB ==============
const ActivitiesTab = ({ activities, setActivities, isPro = false, events = [], familyMembers = [] }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: 'hsl(142, 71%, 45%)', icon: '⚽' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, activityId: null, activityName: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Get stats for each activity
  const getActivityStats = (activityId) => {
    const activityEvents = events.filter(e => e.activity_id === activityId);
    const uniqueChildIds = [...new Set(activityEvents.map(e => e.child_id).filter(Boolean))];
    const activityChildren = familyMembers.filter(m => uniqueChildIds.includes(m.id));
    return {
      eventCount: activityEvents.length,
      children: activityChildren
    };
  };

  const filteredActivities = fuzzySearch(
    searchQuery,
    selectedCategory === 'all' 
      ? ALL_ACTIVITIES 
      : ALL_ACTIVITIES.filter(a => a.category === selectedCategory),
    ['name', 'category']
  );

  const categories = ['all', ...Object.keys(ACTIVITY_CATALOG)];

  const selectActivity = (activity) => {
    setFormData({ name: activity.name, color: activity.color, icon: activity.icon });
    setShowCustomInput(false);
  };

  const openDialog = (activity = null) => {
    setSearchQuery('');
    setSelectedCategory('all');
    setShowCustomInput(false);
    if (activity) {
      setEditingActivity(activity);
      setFormData({ name: activity.name, color: activity.color || 'hsl(142, 71%, 45%)', icon: activity.icon || '⚽' });
      setShowCustomInput(true); // Show custom input when editing
    } else {
      setEditingActivity(null);
      setFormData({ name: '', color: 'hsl(142, 71%, 45%)', icon: '⚽' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Please enter an activity name'); return; }
    try {
      const submitData = { name: formData.name, color: formData.color, icon: formData.icon };
      if (editingActivity) {
        const updated = await updateActivity(editingActivity.id, submitData);
        setActivities(prev => prev.map(a => a.id === editingActivity.id ? updated : a));
        toast.success('Updated successfully');
      } else {
        const created = await createActivity(submitData);
        setActivities(prev => [...prev, created]);
        toast.success('Activity created!');
      }
      setShowDialog(false);
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteActivity(id);
      setActivities(prev => prev.filter(a => a.id !== id));
      toast.success('Deleted');
      setDeleteConfirm({ open: false, activityId: null, activityName: '' });
    } catch (error) { toast.error('Failed to delete'); }
  };

  const openDeleteConfirm = (activity) => {
    setDeleteConfirm({ open: true, activityId: activity.id, activityName: activity.name });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{activities.length} activit{activities.length !== 1 ? 'ies' : 'y'}</p>
        <Button onClick={() => openDialog()} className="rounded-xl h-10 text-sm" data-testid="add-activity-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Activity
        </Button>
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center">
            <Dumbbell className="h-8 w-8 text-green-500" />
          </div>
          <p className="text-gray-600 font-medium mb-1">No activities yet</p>
          <p className="text-gray-400 text-sm mb-4">Add activities your kids participate in</p>
          <Button onClick={() => openDialog()} variant="outline" className="rounded-xl">
            <Plus className="mr-2 h-4 w-4" /> Add First Activity
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {activities.map((activity) => {
            const stats = getActivityStats(activity.id);
            return (
            <FloatingWidget key={activity.id} className="p-4 relative">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ backgroundColor: activity.color }}>
                  {activity.icon || '⚽'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-gray-900 text-sm truncate">{activity.name}</div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                      {stats.eventCount} event{stats.eventCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  {/* Children linked to this activity */}
                  {stats.children.length > 0 ? (
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="flex -space-x-2">
                        {stats.children.slice(0, 4).map(child => (
                          <div 
                            key={child.id}
                            className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-white text-[10px] font-bold"
                            style={{ backgroundColor: child.color }}
                            title={child.name}
                          >
                            {child.name?.charAt(0)}
                          </div>
                        ))}
                        {stats.children.length > 4 && (
                          <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center text-gray-600 text-[10px] font-bold">
                            +{stats.children.length - 4}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {stats.children.map(c => c.name).join(', ')}
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-2 italic">No kids assigned yet</p>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl flex-shrink-0"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openDialog(activity)}><Edit2 className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openDeleteConfirm(activity)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </FloatingWidget>
          );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl mx-4 rounded-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="text-lg">{editingActivity ? 'Edit Activity' : 'Add Activity'}</DialogTitle></DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input 
                placeholder="Search activities... (e.g., soccer, dance, swimming)" 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="pl-10 h-11 rounded-xl"
              />
            </div>

            {/* Category Filter - Dropdown */}
            <div className="flex gap-2 items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 rounded-xl justify-between min-w-[200px]">
                    <span className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      {selectedCategory === 'all' ? 'All Categories' : selectedCategory}
                    </span>
                    <ChevronRight className="h-4 w-4 rotate-90 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                        selectedCategory === 'all' 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="w-5 h-5 rounded flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs">✦</span>
                      All Categories
                      {selectedCategory === 'all' && <Check className="h-4 w-4 ml-auto" />}
                    </button>
                    {categories.filter(c => c !== 'all').map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all text-left ${
                          selectedCategory === cat 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <span className="w-5 h-5 rounded flex items-center justify-center bg-gray-100 text-xs">
                          {ALL_ACTIVITIES.find(a => a.category === cat)?.icon || '📂'}
                        </span>
                        {cat}
                        {selectedCategory === cat && <Check className="h-4 w-4 ml-auto" />}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              {selectedCategory !== 'all' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedCategory('all')}
                  className="h-8 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Selected Activity Preview */}
            {formData.name && (
              <div className="p-4 bg-purple-50 rounded-xl flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: formData.color }}>
                  {formData.icon}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{formData.name}</p>
                  <p className="text-sm text-gray-500">Selected activity</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setFormData({ name: '', color: 'hsl(142, 71%, 45%)', icon: '⚽' })}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Activity Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[300px] overflow-y-auto">
              {filteredActivities.slice(0, 50).map((activity, idx) => (
                <button
                  key={`${activity.name}-${idx}`}
                  onClick={() => selectActivity(activity)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all hover:bg-gray-50 ${
                    formData.name === activity.name ? 'ring-2 ring-purple-500 bg-purple-50' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ backgroundColor: activity.color }}>
                    {activity.icon}
                  </div>
                  <span className="text-[11px] text-gray-700 text-center leading-tight truncate w-full">{activity.name}</span>
                </button>
              ))}
            </div>

            {/* Custom Activity Option */}
            <div className="border-t pt-4">
              <button 
                onClick={() => {
                  if (!isPro) {
                    toast.error('Custom activities are available for Pro users only');
                    return;
                  }
                  setShowCustomInput(!showCustomInput);
                }}
                className={`w-full flex items-center justify-between p-3 rounded-xl border-2 border-dashed transition-all ${
                  isPro ? 'border-purple-300 hover:border-purple-500 hover:bg-purple-50' : 'border-gray-200 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                    <Plus className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Custom Activity</p>
                    <p className="text-xs text-gray-500">Create your own activity</p>
                  </div>
                </div>
                {!isPro && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full text-xs font-medium">
                    <Crown className="h-3 w-3" /> Pro
                  </span>
                )}
              </button>

              {showCustomInput && isPro && (
                <div className="mt-3 p-4 bg-gray-50 rounded-xl space-y-3">
                  <Input 
                    placeholder="Activity name" 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    className="h-10 rounded-xl"
                  />
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 p-3 bg-gray-50">
                    <div className="space-y-3">
                      {/* Sports */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sports</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '⛳', '🥊', '🥋', '🤼'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                      {/* Water & Winter */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Water & Winter</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['🏊', '🤽', '🚣', '🏄', '🤿', '⛷️', '🏂', '⛸️', '🥌', '🛷', '🚤', '⛵', '🛶', '🎿'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                      {/* Fitness */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Fitness & Running</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['🏃', '🚴', '🧘', '🏋️', '🤸', '🚶', '🧗', '🤾', '🏌️', '🤺', '🛹', '🛼', '🛴', '🏍️'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                      {/* Arts & Music */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Arts & Music</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['🎵', '🎶', '🎹', '🎸', '🎺', '🎷', '🥁', '🎻', '🪗', '🎤', '💃', '🕺', '🩰', '🎭', '🎨', '🖼️', '✏️', '🖌️', '📷', '🎬', '🎥', '🎞️'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                      {/* Academic */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Academic & Learning</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['📚', '📖', '✍️', '🔬', '🧪', '🔭', '🧮', '💻', '🖥️', '📝', '🎓', '📐', '🗺️', '🌍', '📰', '📓', '🗣️', '🧠', '💡', '📊'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                      {/* Outdoor */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Outdoor & Nature</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['🏕️', '🎣', '🏹', '🐎', '🐕', '🦮', '🌲', '🥾', '🧭', '🔥', '⛺', '🌄', '🏔️', '🌊', '🌿', '🦋'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                      {/* Games */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Games & Recreation</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['🎮', '🎲', '🃏', '♟️', '🧩', '🎯', '🎳', '🎰', '🪀', '🪁', '🎪', '🎢', '🎡', '🎠', '🏰', '🎁'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                      {/* Other */}
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Other</p>
                        <div className="flex gap-1.5 flex-wrap">
                          {['🚀', '✈️', '🚗', '🧵', '🪡', '🏆', '🥇', '🥈', '🥉', '🎖️', '✨', '⭐', '🌟', '💪', '❤️', '🙏'].map(icon => (
                            <button key={icon} type="button" onClick={() => setFormData({ ...formData, icon })} className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${formData.icon === icon ? 'bg-purple-100 ring-2 ring-purple-500 scale-110' : 'bg-white hover:bg-gray-100 hover:scale-105'}`}>{icon}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl w-full sm:w-auto">Cancel</Button>
            <Button onClick={handleSubmit} disabled={!formData.name.trim()} className="rounded-xl w-full sm:w-auto" data-testid="save-activity-button">
              <Check className="mr-2 h-4 w-4" />{editingActivity ? 'Update' : 'Add Activity'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, activityId: null, activityName: '' })}>
        <DialogContent className="sm:max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center">
                <Trash2 className="h-7 w-7 text-red-500" />
              </div>
            </div>
            <DialogTitle className="text-lg text-center">Delete Activity</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete <span className="font-semibold text-gray-800">"{deleteConfirm.activityName}"</span>?
            </p>
            <p className="text-gray-400 text-xs mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm({ open: false, activityId: null, activityName: '' })} 
              className="rounded-xl w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleDelete(deleteConfirm.activityId)} 
              className="rounded-xl w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== EVENTS TAB ==============
const EventsTab = ({ events, setEvents, familyMembers, activities, homeAddress }) => {
  // Filter states - multi-select capable
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  
  // Grouping and sorting
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'date', 'child', 'activity', 'status'
  const [sortBy, setSortBy] = useState('date-desc'); // 'date-asc', 'date-desc', 'title', 'activity'
  
  // UI states
  const [showDialog, setShowDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({ 
    title: '', 
    child_id: '', 
    activity_id: '', 
    event_type: 'practice',
    date: new Date(), 
    start_time: '09:00', 
    end_time: '10:00', 
    location: '', 
    location_lat: null,
    location_lon: null,
    notes: '',
    goals: '',
    assists: '',
    score_home: '',
    score_away: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, eventId: null, eventTitle: '' });

  // Status options
  const STATUS_OPTIONS = [
    { id: 'scheduled', name: 'Scheduled', icon: '📅', color: 'bg-blue-100 text-blue-700' },
    { id: 'pending', name: 'Pending', icon: '⏳', color: 'bg-yellow-100 text-yellow-700' },
    { id: 'completed', name: 'Completed', icon: '✅', color: 'bg-green-100 text-green-700' },
    { id: 'skipped', name: 'Skipped', icon: '⏭️', color: 'bg-orange-100 text-orange-700' },
  ];

  // Filter logic
  const filteredEvents = events.filter(event => {
    // Multi-select children filter
    if (selectedChildren.length > 0 && !selectedChildren.includes(event.child_id)) return false;
    // Multi-select activities filter
    if (selectedActivities.length > 0 && !selectedActivities.includes(event.activity_id)) return false;
    // Multi-select status filter
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(event.status || 'scheduled')) return false;
    // Multi-select event types filter
    if (selectedEventTypes.length > 0 && !selectedEventTypes.includes(event.event_type)) return false;
    // Search query
    if (searchQuery && !event.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    // Date range
    if (dateRange.start && new Date(event.date) < dateRange.start) return false;
    if (dateRange.end && new Date(event.date) > dateRange.end) return false;
    return true;
  });

  // Sorting logic
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    switch (sortBy) {
      case 'date-asc':
        return new Date(a.date) - new Date(b.date);
      case 'date-desc':
        return new Date(b.date) - new Date(a.date);
      case 'title':
        return a.title.localeCompare(b.title);
      case 'activity':
        const actA = activities.find(act => act.id === a.activity_id)?.name || '';
        const actB = activities.find(act => act.id === b.activity_id)?.name || '';
        return actA.localeCompare(actB);
      default:
        return new Date(b.date) - new Date(a.date);
    }
  });

  // Grouping logic
  const groupedEvents = () => {
    if (groupBy === 'none') return { 'All Events': sortedEvents };
    
    const groups = {};
    sortedEvents.forEach(event => {
      let key;
      switch (groupBy) {
        case 'date':
          key = format(new Date(event.date), 'EEEE, MMMM d, yyyy');
          break;
        case 'child':
          const child = familyMembers.find(m => m.id === event.child_id);
          key = child?.name || 'Unassigned';
          break;
        case 'activity':
          const activity = activities.find(a => a.id === event.activity_id);
          key = activity ? `${activity.icon || '📅'} ${activity.name}` : 'No Activity';
          break;
        case 'status':
          const status = STATUS_OPTIONS.find(s => s.id === (event.status || 'scheduled'));
          key = `${status?.icon || ''} ${status?.name || 'Unknown'}`;
          break;
        default:
          key = 'All Events';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    });
    return groups;
  };

  // Count stats
  const completedCount = events.filter(e => e.status === 'completed').length;
  const skippedCount = events.filter(e => e.status === 'skipped').length;
  const scheduledCount = events.filter(e => !e.status || e.status === 'scheduled' || e.status === 'pending').length;
  const activeFiltersCount = selectedChildren.length + selectedActivities.length + selectedStatuses.length + selectedEventTypes.length + (dateRange.start ? 1 : 0) + (dateRange.end ? 1 : 0);

  const getChildInfo = (id) => familyMembers.find(m => m.id === id);
  const getActivityInfo = (id) => activities.find(a => a.id === id);
  const getEventType = (id) => EVENT_TYPES.find(t => t.id === id) || EVENT_TYPES[0];
  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const eventTypeSupportsStats = (type) => ['game', 'tournament', 'scrimmage', 'competition'].includes(type);

  // Open location in maps app
  const openInMaps = (event) => {
    if (!event.location) return;
    
    const { location_lat, location_lon, location } = event;
    
    // Try to detect mobile and open appropriate app
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (location_lat && location_lon) {
      // If we have coordinates, use them
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${location_lat},${location_lon}`;
      window.open(googleMapsUrl, '_blank');
    } else {
      // Fallback to searching by address
      const address = encodeURIComponent(location);
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${address}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const openDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({ 
        title: event.title, 
        child_id: event.child_id || '', 
        activity_id: event.activity_id || '', 
        event_type: event.event_type || 'practice',
        date: new Date(event.date), 
        start_time: event.start_time, 
        end_time: event.end_time, 
        location: event.location || '', 
        location_lat: event.location_lat || null,
        location_lon: event.location_lon || null,
        notes: event.notes || '',
        goals: event.goals?.toString() || '',
        assists: event.assists?.toString() || '',
        score_home: event.score_home?.toString() || '',
        score_away: event.score_away?.toString() || ''
      });
    } else {
      setEditingEvent(null);
      setFormData({ 
        title: '', 
        child_id: '', 
        activity_id: '', 
        event_type: 'practice',
        date: new Date(), 
        start_time: '09:00', 
        end_time: '10:00', 
        location: '', 
        location_lat: null,
        location_lon: null,
        notes: '',
        goals: '',
        assists: '',
        score_home: '',
        score_away: ''
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) { toast.error('Please enter an event title'); return; }
    try {
      const eventData = { 
        ...formData, 
        date: format(formData.date, 'yyyy-MM-dd'), 
        source: 'manual',
        goals: formData.goals ? parseInt(formData.goals) : null,
        assists: formData.assists ? parseInt(formData.assists) : null,
        score_home: formData.score_home ? parseInt(formData.score_home) : null,
        score_away: formData.score_away ? parseInt(formData.score_away) : null,
      };
      if (editingEvent) {
        const updated = await updateEvent(editingEvent.id, eventData);
        setEvents(prev => prev.map(e => e.id === editingEvent.id ? updated : e));
        toast.success('Event updated');
      } else {
        const created = await createEvent(eventData);
        setEvents(prev => [...prev, created]);
        toast.success('Event created!');
      }
      // BUG-4: Signal that events have changed so Dashboard can refresh AI summary
      localStorage.setItem('eventsLastModified', Date.now().toString());
      setShowDialog(false);
    } catch (error) { toast.error('Failed to save event'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success('Event deleted');
      // BUG-4: Signal that events have changed so Dashboard can refresh AI summary
      localStorage.setItem('eventsLastModified', Date.now().toString());
      setDeleteConfirm({ open: false, eventId: null, eventTitle: '' });
    } catch (error) { toast.error('Failed to delete event'); }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await updateEventStatus(eventId, newStatus);
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, status: newStatus } : e));
      toast.success(newStatus === 'completed' ? 'Marked complete!' : newStatus === 'skipped' ? 'Marked skipped' : 'Status reset');
    } catch (error) { 
      toast.error('Failed to update status'); 
    }
  };

  const openDeleteConfirm = (event) => {
    setDeleteConfirm({ open: true, eventId: event.id, eventTitle: event.title });
  };

  const clearAllFilters = () => {
    setSelectedChildren([]);
    setSelectedActivities([]);
    setSelectedStatuses([]);
    setSelectedEventTypes([]);
    setDateRange({ start: null, end: null });
    setSearchQuery('');
  };

  const toggleChildFilter = (childId) => {
    setSelectedChildren(prev => 
      prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]
    );
  };

  const toggleActivityFilter = (activityId) => {
    setSelectedActivities(prev => 
      prev.includes(activityId) ? prev.filter(id => id !== activityId) : [...prev, activityId]
    );
  };

  const toggleStatusFilter = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const toggleEventTypeFilter = (type) => {
    setSelectedEventTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const groups = groupedEvents();

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-3">
        <FloatingWidget className="p-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <CalendarIcon className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{scheduledCount}</p>
              <p className="text-[10px] text-gray-500">Scheduled</p>
            </div>
          </div>
        </FloatingWidget>
        <FloatingWidget className="p-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{completedCount}</p>
              <p className="text-[10px] text-gray-500">Completed</p>
            </div>
          </div>
        </FloatingWidget>
        <FloatingWidget className="p-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <XCircle className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-900">{skippedCount}</p>
              <p className="text-[10px] text-gray-500">Skipped</p>
            </div>
          </div>
        </FloatingWidget>
      </div>

      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{filteredEvents.length} of {events.length} event{events.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => openDialog()} className="rounded-xl h-10 text-sm" data-testid="add-event-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Event
        </Button>
      </div>

      {/* Search & Filter Toggle */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search events..." 
              className="pl-10 h-11 rounded-xl bg-white" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
            />
          </div>
          <Button 
            variant={showFilters ? 'default' : 'outline'} 
            onClick={() => setShowFilters(!showFilters)}
            className="h-11 rounded-xl px-4"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded-full text-xs font-bold">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </div>

        {/* Expanded Filters Panel */}
        {showFilters && (
          <FloatingWidget className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Filter className="h-4 w-4" /> Advanced Filters
              </h4>
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs h-7">
                  Clear All
                </Button>
              )}
            </div>

            {/* Children Multi-Select */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Filter by Child</label>
              <div className="flex flex-wrap gap-2">
                {familyMembers.map(child => (
                  <button
                    key={child.id}
                    onClick={() => toggleChildFilter(child.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedChildren.includes(child.id)
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: child.color }} />
                    {child.name}
                    {selectedChildren.includes(child.id) && <X className="h-3 w-3 ml-1" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Activities Multi-Select */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Filter by Activity</label>
              <div className="flex flex-wrap gap-2">
                {activities.map(activity => (
                  <button
                    key={activity.id}
                    onClick={() => toggleActivityFilter(activity.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedActivities.includes(activity.id)
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={selectedActivities.includes(activity.id) ? { backgroundColor: activity.color || '#5DADE2' } : {}}
                  >
                    {activity.icon} {activity.name}
                    {selectedActivities.includes(activity.id) && <X className="h-3 w-3 ml-1" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Multi-Select */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Filter by Status</label>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map(status => {
                  const isSelected = selectedStatuses.includes(status.id);
                  // Define explicit selected colors for each status
                  const selectedColorMap = {
                    'scheduled': 'bg-blue-600 text-white',
                    'pending': 'bg-yellow-600 text-white',
                    'completed': 'bg-green-600 text-white',
                    'skipped': 'bg-orange-600 text-white'
                  };
                  return (
                    <button
                      key={status.id}
                      onClick={() => toggleStatusFilter(status.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected ? selectedColorMap[status.id] : status.color
                      }`}
                    >
                      {status.icon} {status.name}
                      {isSelected && <X className="h-3 w-3 ml-1" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Event Type Multi-Select */}
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Filter by Event Type</label>
              <div className="flex flex-wrap gap-2">
                {EVENT_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => toggleEventTypeFilter(type.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      selectedEventTypes.includes(type.id)
                        ? 'bg-gray-900 text-white'
                        : type.color
                    }`}
                  >
                    {type.icon} {type.name}
                    {selectedEventTypes.includes(type.id) && <X className="h-3 w-3 ml-1" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">From Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-10 rounded-xl justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.start ? format(dateRange.start, 'MMM d, yyyy') : 'Start date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.start}
                      onSelect={(date) => setDateRange(prev => ({ ...prev, start: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">To Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-10 rounded-xl justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.end ? format(dateRange.end, 'MMM d, yyyy') : 'End date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateRange.end}
                      onSelect={(date) => setDateRange(prev => ({ ...prev, end: date }))}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </FloatingWidget>
        )}

        {/* Grouping & Sorting Controls */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="w-auto min-w-[130px] h-10 rounded-xl bg-white text-sm">
              <Layers className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Grouping</SelectItem>
              <SelectItem value="date">Group by Date</SelectItem>
              <SelectItem value="child">Group by Child</SelectItem>
              <SelectItem value="activity">Group by Activity</SelectItem>
              <SelectItem value="status">Group by Status</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-auto min-w-[140px] h-10 rounded-xl bg-white text-sm">
              <ArrowUpDown className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Newest First</SelectItem>
              <SelectItem value="date-asc">Oldest First</SelectItem>
              <SelectItem value="title">By Title</SelectItem>
              <SelectItem value="activity">By Activity</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Events List with Grouping */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
            <ListTodo className="h-8 w-8 text-purple-500" />
          </div>
          <p className="text-gray-600 font-medium mb-1">{events.length === 0 ? 'No events yet' : 'No matching events'}</p>
          <p className="text-gray-400 text-sm mb-4">
            {events.length === 0 ? 'Create events or import from your calendar' : 'Try adjusting your filters'}
          </p>
          {activeFiltersCount > 0 && (
            <Button onClick={clearAllFilters} variant="outline" className="rounded-xl mr-2">
              Clear Filters
            </Button>
          )}
          {events.length === 0 && (
            <Button onClick={() => openDialog()} variant="outline" className="rounded-xl">
              <Plus className="mr-2 h-4 w-4" /> Add Event
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([groupName, groupEvents]) => (
            <div key={groupName}>
              {groupBy !== 'none' && (
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">{groupName}</h3>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {groupEvents.length}
                  </span>
                </div>
              )}
              <div className="space-y-3">
                {groupEvents.map((event) => {
                  const child = getChildInfo(event.child_id);
                  const activity = getActivityInfo(event.activity_id);
                  const eventType = getEventType(event.event_type);
                  const eventColor = child?.color || activity?.color || '#5DADE2';
                  const hasStats = event.goals || event.assists || (event.score_home !== null && event.score_away !== null);
                  const isCompleted = event.status === 'completed';
                  const isSkipped = event.status === 'skipped';
                  
                  return (
                    <FloatingWidget key={event.id} className={`p-4 ${isCompleted ? 'opacity-75' : ''} ${isSkipped ? 'opacity-60' : ''}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-1 min-h-[50px] rounded-full flex-shrink-0 self-stretch" style={{ backgroundColor: eventColor }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className={`font-semibold text-gray-900 truncate ${isCompleted ? 'line-through' : ''}`}>{event.title}</h3>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${eventType.color}`}>
                                  {eventType.icon} {eventType.name}
                                </span>
                                {isCompleted && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                    ✅ Done
                                  </span>
                                )}
                                {isSkipped && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                                    ⏭️ Skipped
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-gray-500">
                                <span>{format(new Date(event.date), 'MMM d, yyyy')}</span>
                                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatTime12h(event.start_time)}</span>
                              </div>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {child && <span className="flex items-center gap-1.5 text-xs bg-gray-100 px-2 py-1 rounded-lg"><div className="h-2 w-2 rounded-full" style={{ backgroundColor: child.color }} />{child.name}</span>}
                                {activity && <span className="text-xs text-white px-2 py-1 rounded-lg" style={{ backgroundColor: activity.color }}>{activity.icon || '⚽'} {activity.name}</span>}
                                {event.location && (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openInMaps(event); }}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
                                    data-testid={`event-location-btn-${event.id}`}
                                  >
                                    <MapPin className="h-3 w-3" />
                                    <span className="max-w-[150px] truncate">{event.location.split(',')[0]}</span>
                                    <Navigation className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                              {hasStats && (
                                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                                  {(event.score_home !== null && event.score_away !== null) && (
                                    <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                                      event.score_home > event.score_away 
                                        ? 'bg-green-100 text-green-700' 
                                        : event.score_home < event.score_away 
                                          ? 'bg-red-100 text-red-700' 
                                          : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {event.score_home > event.score_away ? 'W' : event.score_home < event.score_away ? 'L' : 'T'} {event.score_home}-{event.score_away}
                                    </span>
                                  )}
                                  {event.goals > 0 && <span className="text-xs text-gray-600">⚽ {event.goals} goals</span>}
                                  {event.assists > 0 && <span className="text-xs text-gray-600">🎯 {event.assists} assists</span>}
                                </div>
                              )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl flex-shrink-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {!isCompleted && !isSkipped && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'completed')}>
                                      <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" /> Mark Complete
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'skipped')}>
                                      <XCircle className="mr-2 h-4 w-4 text-orange-600" /> Mark Skipped
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                {(isCompleted || isSkipped) && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'scheduled')}>
                                      <RotateCcw className="mr-2 h-4 w-4" /> Reset Status
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => openDialog(event)}>
                                  <Edit2 className="mr-2 h-4 w-4" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openDeleteConfirm(event)} className="text-destructive">
                                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </FloatingWidget>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Event Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg mx-4 rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-lg">{editingEvent ? 'Edit Event' : 'Add Event'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Event Title</Label>
              <Input placeholder="e.g., Soccer Practice" data-testid="event-title-input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="h-11 rounded-xl" />
            </div>
            
            {/* Event Type */}
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Event Type</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {EVENT_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, event_type: type.id })}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all text-xs ${
                      formData.event_type === type.id 
                        ? 'bg-purple-100 ring-2 ring-purple-500' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-lg">{type.icon}</span>
                    <span className="text-[10px] font-medium">{type.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label className="text-sm font-medium">Child</Label>
                <Select value={formData.child_id} onValueChange={(v) => setFormData({ ...formData, child_id: v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{familyMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-sm font-medium">Activity</Label>
                <Select value={formData.activity_id} onValueChange={(v) => setFormData({ ...formData, activity_id: v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{activities.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Date</Label>
              <Popover>
                <PopoverTrigger asChild><Button variant="outline" className="justify-start h-11 rounded-xl"><CalendarIcon className="mr-2 h-4 w-4" />{format(formData.date, 'PPP')}</Button></PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={formData.date} onSelect={(d) => d && setFormData({ ...formData, date: d })} /></PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2"><Label className="text-sm font-medium">Start Time</Label><Input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} className="h-11 rounded-xl" /></div>
              <div className="grid gap-2"><Label className="text-sm font-medium">End Time</Label><Input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} className="h-11 rounded-xl" /></div>
            </div>
            <div className="grid gap-2">
              <Label className="text-sm font-medium">Location</Label>
              <AddressSearchInput
                value={formData.location}
                onChange={(address, coords) => setFormData({ 
                  ...formData, 
                  location: address,
                  location_lat: coords?.lat || null,
                  location_lon: coords?.lon || null
                })}
                placeholder="Search for event location..."
                homeAddress={homeAddress}
                eventStartTime={formData.start_time}
                showTravelTime={true}
              />
            </div>
            
            {/* Stats Fields - Only show for games/tournaments/competitions */}
            {eventTypeSupportsStats(formData.event_type) && (
              <div className="border-t pt-4 mt-2">
                <Label className="text-sm font-medium flex items-center gap-2 mb-3">
                  <Trophy className="h-4 w-4 text-yellow-500" />
                  Game Stats (Optional)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label className="text-xs text-gray-500">Goals/Points Scored</Label>
                    <Input 
                      type="number" 
                      min="0"
                      placeholder="0" 
                      value={formData.goals} 
                      onChange={(e) => setFormData({ ...formData, goals: e.target.value })} 
                      className="h-10 rounded-xl" 
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-xs text-gray-500">Assists</Label>
                    <Input 
                      type="number" 
                      min="0"
                      placeholder="0" 
                      value={formData.assists} 
                      onChange={(e) => setFormData({ ...formData, assists: e.target.value })} 
                      className="h-10 rounded-xl" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="grid gap-2">
                    <Label className="text-xs text-gray-500">Our Score (Home)</Label>
                    <Input 
                      type="number" 
                      min="0"
                      placeholder="0" 
                      value={formData.score_home} 
                      onChange={(e) => setFormData({ ...formData, score_home: e.target.value })} 
                      className="h-10 rounded-xl" 
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-xs text-gray-500">Opponent Score (Away)</Label>
                    <Input 
                      type="number" 
                      min="0"
                      placeholder="0" 
                      value={formData.score_away} 
                      onChange={(e) => setFormData({ ...formData, score_away: e.target.value })} 
                      className="h-10 rounded-xl" 
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-2"><Label className="text-sm font-medium">Notes</Label><Textarea placeholder="Additional notes..." data-testid="event-notes-input" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="rounded-xl min-h-[80px]" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl w-full sm:w-auto">Cancel</Button>
            <Button onClick={handleSubmit} className="rounded-xl w-full sm:w-auto" data-testid="event-submit-button"><Check className="mr-2 h-4 w-4" />{editingEvent ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, eventId: null, eventTitle: '' })}>
        <DialogContent className="sm:max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center">
                <Trash2 className="h-7 w-7 text-red-500" />
              </div>
            </div>
            <DialogTitle className="text-lg text-center">Delete Event</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete <span className="font-semibold text-gray-800">"{deleteConfirm.eventTitle}"</span>?
            </p>
            <p className="text-gray-400 text-xs mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm({ open: false, eventId: null, eventTitle: '' })} 
              className="rounded-xl w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleDelete(deleteConfirm.eventId)} 
              className="rounded-xl w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== IMPORT TAB ==============
const ImportTab = ({ familyMembers, onImportComplete }) => {
  const fileInputRef = useRef(null);
  const [calendarSources, setCalendarSources] = useState([]);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [calendarName, setCalendarName] = useState('');
  const [calendarChildId, setCalendarChildId] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, sourceId: null, sourceName: '' });

  useEffect(() => {
    loadCalendarSources();
  }, []);

  const loadCalendarSources = async () => {
    try {
      const sources = await fetchCalendarSources();
      setCalendarSources(sources);
    } catch (error) { console.error('Failed to load calendar sources'); }
  };

  const handleSubscribe = async () => {
    if (!calendarUrl.trim()) { toast.error('Please enter a calendar URL'); return; }
    if (!calendarName.trim()) { toast.error('Please enter a name'); return; }
    setSubscribing(true);
    try {
      await subscribeCalendar({ name: calendarName, url: calendarUrl, child_id: calendarChildId || null });
      toast.success('Calendar connected!');
      setCalendarUrl(''); setCalendarName(''); setCalendarChildId('');
      loadCalendarSources();
      onImportComplete();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to connect'); }
    finally { setSubscribing(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.toLowerCase().endsWith('.ics')) { toast.error('Please select an .ics file'); return; }
    setUploading(true);
    try {
      await uploadICalFile(file, file.name.replace('.ics', ''), null);
      toast.success('Calendar imported!');
      loadCalendarSources();
      onImportComplete();
    } catch (error) { toast.error('Failed to upload'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDelete = async (id) => {
    try { 
      await deleteCalendarSource(id); 
      toast.success('Removed'); 
      loadCalendarSources(); 
      setDeleteConfirm({ open: false, sourceId: null, sourceName: '' });
    }
    catch (error) { toast.error('Failed to remove'); }
  };

  const openDeleteConfirm = (source) => {
    setDeleteConfirm({ open: true, sourceId: source.id, sourceName: source.name });
  };

  return (
    <div className="space-y-4">
      {/* Connect by URL */}
      <FloatingWidget className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <CloudDownload className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Connect by URL</h3>
            <p className="text-xs text-gray-500">Google Calendar, Outlook, etc.</p>
          </div>
        </div>
        <div className="space-y-3">
          <Input placeholder="Calendar name" value={calendarName} onChange={e => setCalendarName(e.target.value)} className="h-11 rounded-xl" />
          <Input placeholder="https://calendar.google.com/..." value={calendarUrl} onChange={e => setCalendarUrl(e.target.value)} className="h-11 rounded-xl" />
          <Select value={calendarChildId} onValueChange={setCalendarChildId}>
            <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Assign to child (optional)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No specific child</SelectItem>
              {familyMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleSubscribe} disabled={subscribing} className="w-full rounded-xl h-11">
            {subscribing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
            {subscribing ? 'Connecting...' : 'Connect Calendar'}
          </Button>
        </div>
      </FloatingWidget>

      {/* Upload File */}
      <FloatingWidget className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center">
            <FileUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Upload .ics File</h3>
            <p className="text-xs text-gray-500">Import from exported file</p>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept=".ics" onChange={handleFileUpload} className="hidden" />
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" className="w-full rounded-xl h-11 border-dashed border-2">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          {uploading ? 'Uploading...' : 'Choose File'}
        </Button>
      </FloatingWidget>

      {/* Connected Calendars */}
      {calendarSources.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-sm text-gray-700">Connected Calendars</h3>
          {calendarSources.map(source => (
            <FloatingWidget key={source.id} className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${source.sync_status === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                    <CalendarIcon className={`h-4 w-4 ${source.sync_status === 'success' ? 'text-green-600' : 'text-red-600'}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{source.name}</p>
                    <p className="text-xs text-gray-500">{source.event_count} events</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm(source)} className="rounded-xl text-red-500 h-8 w-8 p-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </FloatingWidget>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, sourceId: null, sourceName: '' })}>
        <DialogContent className="sm:max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center">
                <Trash2 className="h-7 w-7 text-red-500" />
              </div>
            </div>
            <DialogTitle className="text-lg text-center">Remove Calendar</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <p className="text-gray-600 text-sm">
              Are you sure you want to remove <span className="font-semibold text-gray-800">"{deleteConfirm.sourceName}"</span>?
            </p>
            <p className="text-gray-400 text-xs mt-2">All imported events from this source will be deleted.</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm({ open: false, sourceId: null, sourceName: '' })} 
              className="rounded-xl w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleDelete(deleteConfirm.sourceId)} 
              className="rounded-xl w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Remove Calendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== HISTORY TAB ==============
const HistoryTab = ({ familyMembers }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'completed', 'skipped'

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const historyEvents = await fetchEventHistory();
      setEvents(historyEvents);
    } catch (error) {
      console.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleResetStatus = async (eventId) => {
    try {
      await updateEventStatus(eventId, 'pending');
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success('Event restored to pending');
    } catch (error) {
      toast.error('Failed to reset event');
    }
  };

  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true;
    return event.status === filter;
  });

  const completedCount = events.filter(e => e.status === 'completed').length;
  const skippedCount = events.filter(e => e.status === 'skipped').length;

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <FloatingWidget className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
              <p className="text-xs text-gray-500">Completed</p>
            </div>
          </div>
        </FloatingWidget>
        <FloatingWidget className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <XCircle className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{skippedCount}</p>
              <p className="text-xs text-gray-500">Skipped</p>
            </div>
          </div>
        </FloatingWidget>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          className="rounded-xl"
        >
          All ({events.length})
        </Button>
        <Button
          variant={filter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('completed')}
          className="rounded-xl"
        >
          <CheckCircle2 className="mr-1 h-3 w-3" /> Completed
        </Button>
        <Button
          variant={filter === 'skipped' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('skipped')}
          className="rounded-xl"
        >
          <XCircle className="mr-1 h-3 w-3" /> Skipped
        </Button>
      </div>

      {/* Events List */}
      {filteredEvents.length === 0 ? (
        <FloatingWidget className="p-8 text-center">
          <History className="h-10 w-10 mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 text-sm">No event history yet</p>
          <p className="text-gray-400 text-xs mt-1">
            Events marked as complete or skipped will appear here
          </p>
        </FloatingWidget>
      ) : (
        <div className="space-y-2">
          {filteredEvents.map(event => {
            const eventColor = event.child?.color || '#5DADE2';
            const eventDate = event.date ? parseISO(event.date) : new Date();
            
            return (
              <FloatingWidget key={event.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
                    style={{ backgroundColor: eventColor }}
                  >
                    {event.child?.name?.charAt(0) || event.title?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 text-sm truncate">{event.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        event.status === 'completed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {event.status === 'completed' ? 'Completed' : 'Skipped'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        {format(eventDate, 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {event.start_time}
                      </span>
                      {event.child && (
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: event.child.color }} />
                          {event.child.name}
                        </span>
                      )}
                    </div>
                    {event.location && (
                      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {event.location}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResetStatus(event.id)}
                    className="rounded-xl text-gray-400 hover:text-gray-600 h-8 w-8 p-0"
                    title="Restore to pending"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </FloatingWidget>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== MAIN PAGE ==============
const MyFamilyPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [events, setEvents] = useState([]);
  const [accountStatus, setAccountStatus] = useState(null);
  const [homeAddress, setHomeAddress] = useState(null);
  
  const initialTab = searchParams.get('tab') || 'family';
  const [activeTab, setActiveTab] = useState(initialTab);

  const isPro = accountStatus?.account_type === 'pro';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [membersData, activitiesData, eventsData, accountData, settingsData] = await Promise.all([
      fetchFamilyMembers().catch(err => { console.error('Failed to fetch family members:', err); return []; }),
      fetchActivities().catch(err => { console.error('Failed to fetch activities:', err); return []; }),
      fetchEvents().catch(err => { console.error('Failed to fetch events:', err); return []; }),
      api.get('/account/status').then(r => r.data).catch(() => null),
      api.get('/settings').then(r => r.data.settings).catch(() => null)
    ]);
    setFamilyMembers(membersData || []);
    setActivities(activitiesData || []);
    setEvents(eventsData || []);
    setAccountStatus(accountData);
    if (settingsData?.home_address) {
      setHomeAddress(settingsData.home_address);
    }
    setLoading(false);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Family</h1>
        <p className="text-sm text-gray-500">Manage your family, activities & schedules</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-full p-1 rounded-xl bg-gray-100 grid grid-cols-5 h-auto mb-6">
          <TabsTrigger value="family" className="rounded-lg py-2.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Users className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Family</span>
          </TabsTrigger>
          <TabsTrigger value="activities" className="rounded-lg py-2.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Dumbbell className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Activities</span>
          </TabsTrigger>
          <TabsTrigger value="events" className="rounded-lg py-2.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <CalendarIcon className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Events</span>
          </TabsTrigger>
          <TabsTrigger value="import" className="rounded-lg py-2.5 text-xs font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Upload className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Import</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="family" className="mt-0">
          <FamilyTab members={familyMembers} setMembers={setFamilyMembers} isPro={isPro} events={events} activities={activities} />
        </TabsContent>

        <TabsContent value="activities" className="mt-0">
          <ActivitiesTab activities={activities} setActivities={setActivities} isPro={isPro} events={events} familyMembers={familyMembers} />
        </TabsContent>

        <TabsContent value="events" className="mt-0">
          <EventsTab events={events} setEvents={setEvents} familyMembers={familyMembers} activities={activities} homeAddress={homeAddress} />
        </TabsContent>

        <TabsContent value="import" className="mt-0">
          <ImportTab familyMembers={familyMembers} onImportComplete={loadData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyFamilyPage;
